import { useEffect, useMemo, useRef, useState } from 'react';

interface Section {
  id: string;
  title: string;
  content: React.ReactNode;
}

const SECTIONS: Section[] = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    content: (
      <>
        <p>
          You begin every study from the dashboard. Click <strong>New Study</strong> and you will be
          offered three ways to start a project. Each path is suited to a different situation, and
          the path you choose determines how much of the platform is pre-populated for you.
        </p>
        <h3 className="mt-6 font-semibold text-gray-900">Upload Protocol (recommended)</h3>
        <p>
          This is the recommended path. You upload your study protocol as a PDF, DOC, or DOCX file
          up to 50 MB. The platform reads the protocol end to end and extracts the structured facts
          that every downstream step depends on — the treatment arms, the visit schedule, the
          objectives and endpoints, the inclusion and exclusion criteria, the safety assessments,
          and the interventions. The protocol becomes the source of truth that seeds all subsequent
          generation: the study design, the case report forms, the data dictionary, the informed
          consent form, the data management plan, and the validation package. If you have a
          protocol, this is the path you should take.
        </p>
        <h3 className="mt-6 font-semibold text-gray-900">Upload CRF Packet</h3>
        <p>
          Choose this path when your team has already produced a CRF packet — for example, a PDF
          set of forms inherited from a previous study — and you want to skip protocol analysis.
          The platform reads the packet and extracts the forms, variables, and response options
          directly from the document. You can still attach a protocol later, but the form definitions
          come from the packet rather than from protocol analysis. This path is best when the forms
          are already locked and the protocol is supplementary.
        </p>
        <h3 className="mt-6 font-semibold text-gray-900">Manual Setup</h3>
        <p>
          Choose this path when you want a blank project with no upstream document. Manual Setup is
          appropriate for phased studies that have not yet locked a protocol, for exploratory work,
          or for any case where you want to build the study configuration from scratch. You will
          enter the study arms, visit schedule, and form list by hand. Downstream documents will
          still generate, but they will draw only on what you enter — there is no protocol for them
          to ground against.
        </p>
        <p className="mt-6">
          After you select a path, the project opens at the first workflow step. From there, you
          can move through the seven workflow steps in the left sidebar, or jump directly to any
          step you want to work on. Your progress is saved continuously, so you can leave and come
          back at any time without losing work.
        </p>
      </>
    ),
  },
  {
    id: 'protocol-intelligence',
    title: 'Protocol Intelligence',
    content: (
      <>
        <p>
          Protocol Intelligence is the first step in the workflow. This is where you upload your
          study protocol and where the platform reads the document and extracts the structured
          facts that every other step depends on. The information surfaced here flows forward into
          Study Design, the CRF Builder, the data management deliverables, and the validation
          package, so it is worth spending a few minutes reviewing what the platform extracted
          before you move on.
        </p>
        <h3 className="mt-6 font-semibold text-gray-900">Uploading the protocol</h3>
        <p>
          You upload your protocol as a PDF. The platform stores the file and begins analysis
          immediately. The analysis typically takes between one and two minutes depending on the
          length and complexity of the document. You do not need to stay on the page while it runs.
          You can leave and come back, and the page will show the results when they are ready. If
          the analysis fails for any reason — for example, if the file is corrupted or unreadable —
          a message will appear with an option to try again.
        </p>
        <h3 className="mt-6 font-semibold text-gray-900">What gets extracted</h3>
        <p>
          The platform identifies the study design (phase, blinding, randomization, and treatment
          arms), the objectives and endpoints (primary, secondary, and exploratory), the
          interventions, the population criteria (inclusion and exclusion), the visit schedule, and
          the safety assessments. When the analysis is complete the results appear on the page in
          a structured layout that you can scroll through and review. You should look over this
          summary before moving on, because anything missing here will be missing from the
          generated artifacts downstream.
        </p>
        <h3 className="mt-6 font-semibold text-gray-900">Extract Protocol Requirements</h3>
        <p>
          Once the analysis is complete, you can click <strong>Extract Protocol Requirements</strong>
          to convert the analysis into a typed list of protocol requirements. The requirements are
          organized by type — objectives, endpoints, inclusion criteria, exclusion criteria, safety
          assessments, and similar — and each carries a status of active, superseded, or removed.
          This list feeds the Requirements Traceability Matrix used in the Study Build documents
          downstream. Clicking this button does not re-analyze the protocol; it simply reorganizes
          what was already extracted into the structured requirements format.
        </p>
        <h3 className="mt-6 font-semibold text-gray-900">Run Validation</h3>
        <p>
          The <strong>Run Validation</strong> button checks the generated CRF artifacts against the
          protocol for bidirectional coverage. It verifies two things: that every protocol
          requirement maps to at least one CRF form or variable, and that no CRF content exists
          without a protocol basis. The results are returned by severity, so you can see at a
          glance whether you have gaps or unwarranted content. This action is most useful after the
          CRF Builder step is complete, because it needs forms to compare against. If you run it
          before any forms exist, the report will simply tell you that nothing is in scope yet.
        </p>
        <h3 className="mt-6 font-semibold text-gray-900">Generate All from Protocol</h3>
        <p>
          The <strong>Generate All from Protocol</strong> button is the full pipeline trigger. It
          reads the completed protocol analysis and automatically produces the study design seed
          (arms and visits), a placeholder informed consent form, the initial edit check set, the
          essential documents scaffold, and the Trial Master File skeleton. It then runs an
          automatic quality repair pass to clean up obvious issues. This is the recommended action
          after a successful protocol analysis because it populates every subsequent step with a
          starting point derived from your protocol, so you can begin reviewing rather than
          building from scratch. The pipeline can take several minutes to complete. You can leave
          the page while it runs and return when you are ready.
        </p>
      </>
    ),
  },
  {
    id: 'study-design',
    title: 'Study Design',
    content: (
      <>
        <p>
          Study Design is where you configure the structural parameters of the study: the treatment
          arms, the visit schedule, the randomization method, and the informed consent form. These
          settings are the input contract for everything that follows — CRF generation, visit
          mapping, document generation, and SDTM packaging all read arms and visits from here
          rather than going back to the protocol. Changes you make here propagate forward, so if
          you adjust the arms or visits after generating downstream artifacts, you may need to
          regenerate those artifacts to pick up the changes.
        </p>
        <h3 className="mt-6 font-semibold text-gray-900">Study Arms</h3>
        <p>
          The Study Arms tab lists each treatment arm extracted from the protocol. For each arm you
          will see the arm name, a description, the intervention, and the allocation ratio. You can
          add a new arm, edit any existing arm, or remove an arm that does not apply. The
          <strong> Save</strong> button at the top right of the page commits your changes; nothing
          is persisted until you click Save.
        </p>
        <h3 className="mt-6 font-semibold text-gray-900">Schedule of Events</h3>
        <p>
          The Schedule of Events tab lists each study visit derived from the protocol, including the
          visit name, its timing relative to baseline (for example, Day 1, Week 4, Month 6), and
          the visit window expressed in days before and after the scheduled day. You can add,
          edit, or remove visits. The schedule of events you define here determines which columns
          appear in the Visit and Form Matrix and which visits are referenced by the data
          management deliverables, so it is worth getting right before you move on.
        </p>
        <h3 className="mt-6 font-semibold text-gray-900">Randomization</h3>
        <p>
          The Randomization tab captures the randomization method — for example, stratified block
          randomization, simple randomization, or minimization — along with the allocation ratio
          and any stratification factors. This information is used in the generated Data
          Management Plan, the System Design Specification, and other downstream documents. If your
          study is open-label or non-randomized, you can record that here as well.
        </p>
        <h3 className="mt-6 font-semibold text-gray-900">Consent Form</h3>
        <p>
          The Consent Form tab is where you build the informed consent document section by section.
          The platform generates an initial draft for each section based on the protocol. The
          sections include Purpose, Procedures, Risks, Benefits, Confidentiality, and Voluntary
          Participation, among others. Each section is reviewed and approved independently, and
          you can regenerate any individual section without disturbing the others. Once you have
          approved every section, the full informed consent form is assembled from the approved
          parts.
        </p>
        <h3 className="mt-6 font-semibold text-gray-900">Settings</h3>
        <p>
          The Settings tab contains study-level configuration options for the project. These
          settings apply across all workflow steps.
        </p>
        <h3 className="mt-6 font-semibold text-gray-900">Save</h3>
        <p>
          The <strong>Save</strong> button is always visible at the top right of the page. Click it
          after making any edits to Study Arms, Schedule of Events, or Randomization. Changes do not
          persist until you click Save, and you will see a confirmation when the save completes.
        </p>
      </>
    ),
  },
  {
    id: 'crf-builder',
    title: 'CRF Builder',
    content: (
      <>
        <p>
          The CRF Builder is where you create, review, and approve all of the case report forms for
          your study. You can approach the page in three ways depending on what you are starting
          with: you can generate a complete form list from the protocol you uploaded, you can
          upload an existing CRF packet to extract the forms from a document, or you can import
          form definitions from a REDCap data dictionary.
        </p>
        <h3 className="mt-6 font-semibold text-gray-900">Generate CRF List</h3>
        <p>
          This is the recommended starting point when you have uploaded a protocol. The platform
          reads the procedures, visits, and assessments from your protocol and suggests a complete
          set of forms. Each suggested form comes with a name, a rationale for why the form is
          needed, and a category such as Screening, Demographics, Safety, or Procedures. Generation
          typically takes 15 to 30 seconds. When it completes, the list appears below.
        </p>
        <h3 className="mt-6 font-semibold text-gray-900">Upload CRF Packet</h3>
        <p>
          If you already have a CRF document — for example, a PDF or DOCX packet from a previous
          study — you can upload it here. The platform reads the document and extracts the form
          definitions from the existing packet rather than generating from scratch. This is the
          right choice when your forms are already locked and you want to use them as the starting
          point.
        </p>
        <h3 className="mt-6 font-semibold text-gray-900">Import REDCap Data Dictionary</h3>
        <p>
          If your study uses REDCap, you can import a REDCap data dictionary CSV. This brings in
          the existing form definitions directly, preserving the field names, response options, and
          branching logic from the REDCap project.
        </p>
        <h3 className="mt-6 font-semibold text-gray-900">Working with the form list</h3>
        <p>
          After forms have been generated, uploaded, or imported, you will see a list of all case
          report forms. Each row shows the form name, its CDASH domain tag, its category, its
          status (Draft, In Review, or Final), the number of variables it contains, and a Source
          PDF link if a source document was attached during upload. To open the variable list for
          any form, click the expand arrow on the left side of the row.
        </p>
        <h3 className="mt-6 font-semibold text-gray-900">Header summary badges</h3>
        <p>
          At the top of the page you will see three summary badges. They show the total number of
          forms, the number of forms that have had variables generated, and the number of forms
          that have been approved to Final status. Use these badges to track your overall progress
          through the form set at a glance.
        </p>
        <h3 className="mt-6 font-semibold text-gray-900">Generate All Variables</h3>
        <p>
          Once you have reviewed the form list and removed any forms you do not need, click
          <strong> Generate All Variables</strong>. The platform generates the variable-level field
          definitions for every form in the list, including variable names, question text, response
          types, and coded values. This step can take several minutes depending on the number of
          forms you have. You do not need to stay on the page while it runs — you can leave and
          return when it completes.
        </p>
        <h3 className="mt-6 font-semibold text-gray-900">Working inside a form</h3>
        <p>
          When you expand a form, you see the full variable list with columns for Order, Variable
          name, Question text, and Type. Each variable shows its response type — Free Text, Single
          Select (with the number of options), Yes/No, Date, and so on. Click the expand arrow on
          any variable to see its response options and to edit field-level details such as the
          variable label, question text, or coded value list. At the bottom of the expanded form
          you will find the following controls:
        </p>
        <ul className="list-disc pl-6 mt-3 space-y-2">
          <li>
            <strong>Generate CRF.</strong> Produces the formatted case report form document (Word
            output) for this individual form. Use this when you want to share or print a
            standalone copy of a single form.
          </li>
          <li>
            <strong>Generate aCRF.</strong> Produces the annotated CRF for this form, which overlays
            the SDTM variable annotations onto the form layout. A dropdown lets you choose the
            output format. The annotated CRF is used in regulatory submissions to show how each
            field maps to its SDTM target.
          </li>
          <li>
            <strong>Back to Draft.</strong> If the form is in Final status and requires changes,
            click Back to Draft to reopen it for editing. The form will leave the approved set
            until you mark it Final again.
          </li>
          <li>
            <strong>FINAL badge.</strong> When the badge is visible, this form has been approved
            and is included in the data dictionary and in any downstream documents that depend on
            the approved form set.
          </li>
        </ul>
        <h3 className="mt-6 font-semibold text-gray-900">Resolving duplicate variable names</h3>
        <p>
          Before you can approve a form, the platform checks the variable list across all forms
          for variable names that appear in more than one place. If it finds any, they are surfaced
          in a warning banner at the top of the page. The banner lists each duplicate variable, the
          forms it appears in, and offers three resolution options for each one: <strong>Confirm</strong>{' '}
          when the duplication is intentional (as with repeated-measures instruments collected at
          multiple visits), <strong>Rename</strong> to change the variable name in one of the forms
          to disambiguate it, or <strong>Remove</strong> to delete the variable from one of the
          forms entirely. Resolving duplicates does not block approval, but the warning banner will
          remain visible until every duplicate has been addressed.
        </p>
        <h3 className="mt-6 font-semibold text-gray-900">Additional header buttons</h3>
        <ul className="list-disc pl-6 mt-3 space-y-2">
          <li>
            <strong>Export CRF JSON.</strong> Downloads the complete structured variable list for
            all forms as a JSON file. This is useful for transferring the form definitions to an
            external system or for archiving the current state of your CRF set.
          </li>
          <li>
            <strong>New Protocol.</strong> Resets the CRF Builder to allow you to generate a new
            form list from a different protocol. Use this when the protocol has been substantially
            amended and you want to start the form generation process over.
          </li>
          <li>
            <strong>Add Form.</strong> Manually adds a blank form to the list. Use this to create a
            form that was not suggested by the protocol analysis but that you know you need to
            collect.
          </li>
          <li>
            <strong>Upload CRF.</strong> Uploads an additional CRF document to extract forms from,
            after the initial generation. Forms extracted from the new document are added to the
            existing list.
          </li>
          <li>
            <strong>Import DD (CRF Builder).</strong> Imports forms and variables from a{' '}
            <strong>REDCap</strong> data dictionary export (.csv), for studies whose CRFs were
            built in REDCap first. It is an import from REDCap only. It does not accept the
            data dictionary this platform exports, and it is not a way to edit this study &mdash;
            to change variables, edit the forms here in the CRF Builder and regenerate the data
            dictionary.
          </li>
        </ul>
        <h3 className="mt-6 font-semibold text-gray-900">VIEW tabs</h3>
        <p>
          Two tabs sit at the top of the form area. The <strong>Forms</strong> tab shows the form
          list described above. The <strong>Schedule of Events</strong> tab shows the visit-to-form
          matrix, which maps each form to the visits at which it is collected. Use the schedule of
          events view when you want to see your data collection at a glance by visit rather than
          by form.
        </p>
      </>
    ),
  },
  {
    id: 'study-documents',
    title: 'Study Documents',
    content: (
      <>
        <p>
          Study Documents is the central hub for all data management and regulatory deliverables.
          Each card on the page represents one document. Cards that have already been generated
          show a <strong>Ready</strong> status badge along with the date and time of the last
          generation. Cards that have not yet been generated show a <strong>Generate</strong>{' '}
          button. Every card also has a small circular regenerate icon (a refresh arrow) in the
          corner, which lets you rebuild the document at any time using the latest protocol and
          CRF data.
        </p>
        <p className="mt-3">
          The page is organized into sections. Quality Audit sits at the top of the page and
          applies across all CRF content. Below it, you will find Study Design and Setup, Data
          Management Setup, Study Conduct, and Study Closeout.
        </p>

        <h3 className="mt-6 font-semibold text-gray-900">Quality Audit</h3>
        <p>
          Quality Audit runs a combined deterministic and analysis-based review of your CRF forms.
          It checks for duplicates across the form set, for fields that may not be grounded in the
          protocol, for missing forms required by the protocol assessments, and for protocol
          coverage gaps. Findings are returned by category so you can work through them in order.
          The auto-repair function applies fixes that do not require human judgment — for example,
          collapsing exact duplicates or removing fields that have no protocol basis. Findings
          that need a human decision are surfaced for review.
        </p>

        <h3 className="mt-6 font-semibold text-gray-900">Study Design and Setup</h3>

        <h4 className="mt-4 font-semibold text-gray-800">CRF Instruction Guide</h4>
        <p>
          The CRF Instruction Guide is a field-by-field completion guide written for site staff to
          ensure consistent data entry across sites and visits. The guide covers every form and
          every variable in your approved CRF set and explains how each field should be completed,
          including coded value selection and unit conventions. Click <strong>Download Guide</strong>{' '}
          to save a Word document. The regenerate icon rebuilds the guide from the current CRF
          state, so regenerate after any change to the form set.
        </p>

        <h4 className="mt-4 font-semibold text-gray-800">Informed Consent Form</h4>
        <p>
          The Informed Consent Form is built section by section in Study Design. This card serves
          as a link back to that step. Click <strong>Open Study Design</strong> to navigate there.
          The card reflects the current state of the assembled consent form, so it updates as you
          approve sections.
        </p>

        <h4 className="mt-4 font-semibold text-gray-800">Visit and Form Matrix</h4>
        <p>
          The Visit and Form Matrix is the schedule of which forms are collected at which visits,
          along with whether each form is required or optional at that visit and the visit window
          tolerances. The matrix is currently editable in the Schedule of Events tab of the CRF
          Builder and the Study Design step. Click <strong>Open Study Design</strong> to navigate
          there. A standalone Word export of the matrix is planned for a future release.
        </p>

        <h3 className="mt-6 font-semibold text-gray-900">Data Management Setup</h3>

        <h4 className="mt-4 font-semibold text-gray-800">Data Dictionary</h4>
        <p>
          The Data Dictionary is a CDASH-compliant data dictionary generated from all approved CRF
          forms. Each row represents one variable and includes the domain, the variable name, the
          question text, the response type, the coded values, and the validation rules. The
          dictionary's version number and total field count are shown on the card. Click
          <strong> Download CSV</strong> to save the dictionary as a comma-separated values file,
          or use the Excel export to save it as a spreadsheet. The dictionary only includes forms
          that have been approved in the CRF Builder. If you have unapproved forms, the card will
          tell you how many are excluded from the current version. Regenerate the dictionary after
          you approve any additional forms.
        </p>
        <p className="mt-2">
          <strong>Apply Dictionary Edits.</strong> Export the dictionary, change a label, field
          type, picklist values, help text or required flag in a spreadsheet, and upload the file
          back here to apply those edits to the forms and the dictionary. You are shown every change
          for review before anything is written, and nothing is applied if any row is rejected.
          Leave the two id columns at the end of each row exactly as exported &mdash; they are how a
          row is matched to its question. <strong>Renaming a variable is not supported here</strong>,
          because the variable name is the key used by the SDTM mapping, edit checks, the annotated
          CRF and the ODM export; rename in the CRF Builder instead. This is a different thing from
          the CRF Builder's <strong>Import DD</strong>, which creates forms from a REDCap file.
        </p>

        <h4 className="mt-4 font-semibold text-gray-800">Data Management Plan</h4>
        <p>
          The Data Management Plan is a comprehensive 18-section DMP aligned to the Society for
          Clinical Data Management's Good Clinical Data Management Practices guidance. It covers
          data collection, validation, coding, external data handling, reconciliation, and
          regulatory compliance. Click <strong>View</strong> on the card to open the DMP in an
          inline editor, where you can review and regenerate individual sections independently
          without rebuilding the entire document. The regenerate icon on the card rebuilds the full
          DMP at once. Export to Word is available from within the document view.
        </p>

        <h4 className="mt-4 font-semibold text-gray-800">Edit Checks</h4>
        <p>
          Edit Checks is the programmatic validation rule specification for the study database. It
          includes range checks, consistency checks across fields and forms, completeness
          requirements, logical checks, and calculated field derivations. Click <strong>View</strong>{' '}
          to open the edit check list. Export to CSV or Word is available from within the view.
        </p>

        <h4 className="mt-4 font-semibold text-gray-800">SDTM Mapping Specification</h4>
        <p>
          The SDTM Mapping Specification provides variable-level mapping from your CRF fields to
          CDISC SDTM domains. The mapping is grounded in CDASH IG v2.2 and the relevant SDTMIG
          version, and any variable that cannot be mapped to a known SDTM target is annotated as
          <strong> NOT SUBMITTED</strong> rather than guessed at. The SDTM and SDTMIG versions are
          determined automatically from the study start date against the FDA Data Standards
          Catalog. Click <strong>View</strong> to open the mapping. From within the view you can
          export Define.xml, the Reviewer's Guide, SUPPQUAL templates, and a complete submission
          ZIP package. Generate the Data Dictionary before generating the SDTM Mapping so that the
          variable metadata it operates on is accurate.
        </p>

        <h3 className="mt-6 font-semibold text-gray-900">Study Conduct</h3>

        <h4 className="mt-4 font-semibold text-gray-800">External Data Transfer Specification</h4>
        <p>
          This document defines how data flows from external vendors — central laboratories, IRT,
          ePRO platforms, imaging vendors, and safety databases — into the clinical database. It
          covers per-source variable metadata, the transfer schedule, blinding rules,
          reconciliation procedures, and signature blocks. Click <strong>View</strong> to open the
          document.
        </p>

        <h4 className="mt-4 font-semibold text-gray-800">Medical Coding Specification</h4>
        <p>
          The Medical Coding Specification documents the MedDRA, WHODrug, and LOINC coding
          conventions for your study, including dictionary versions, per-domain coding procedures,
          and the quality control workflow. Click <strong>View</strong> to open the document.
        </p>

        <h4 className="mt-4 font-semibold text-gray-800">Data Review and Cleaning Plan</h4>
        <p>
          The Data Review and Cleaning Plan is a risk-based plan covering ongoing data review, the
          query lifecycle, external data reconciliation, the key risk and quality indicators you
          will track, the cleaning milestones across the study lifecycle, and the database lock
          readiness criteria. It is grounded in ICH E6(R3) and SCDM GCDMP. Click <strong>View</strong>{' '}
          to open the document.
        </p>

        <h4 className="mt-4 font-semibold text-gray-800">SAE Reconciliation Procedure</h4>
        <p>
          The SAE Reconciliation Procedure documents how serious adverse events are reconciled
          between the EDC and the safety database. It includes the data point catalog, the
          discrepancy tier framework, blinding rules, the milestone schedule, and the database lock
          sign-off criteria. It is grounded in ICH E2A, ICH E2B(R3), ICH E6(R3), 21 CFR 312.32, and
          the CDASH SAE Supplement v2.0. Click <strong>View</strong> to open the document.
        </p>

        <h3 className="mt-6 font-semibold text-gray-900">Study Closeout</h3>

        <h4 className="mt-4 font-semibold text-gray-800">Database Lock Checklist</h4>
        <p>
          The Database Lock Checklist is a 19-category checklist covering query closure, coding
          completion, SAE reconciliation, external data reconciliation, deviation review, audit
          trail review, signatures, and post-lock activities. Conditional items are resolved
          against your study scope so that only the checklist items that apply to your study are
          shown. It is grounded in SCDM GCDMP, ICH E6(R3), 21 CFR Part 11, and CDISC submission
          standards. Click <strong>View</strong> to open the checklist.
        </p>
      </>
    ),
  },
  {
    id: 'study-build',
    title: 'Study Build',
    content: (
      <>
        <p>
          Study Build is where you generate the software development lifecycle (SDLC) validation
          and requirements documentation for the EDC build. These documents are what your
          organization needs to qualify the electronic data capture system for this specific study.
          The page is organized into three collapsible sections so you can focus on the part of
          the package you are working on.
        </p>
        <p>
          Each document has a <strong>Generate</strong> button when it has not yet been produced,
          and a <strong>Download</strong> button once it has been generated. A small regenerate
          icon on each card lets you rebuild any document using the latest study data after you
          have made changes upstream.
        </p>

        <h3 className="mt-6 font-semibold text-gray-900">Combined Documents</h3>
        <p>
          Combined Documents are full deliverables that bundle related requirements or design
          content into one document. Most organizations work primarily from these.
        </p>

        <h4 className="mt-4 font-semibold text-gray-800">User Requirements Specification (URS)</h4>
        <p>
          The URS defines what this study must capture and which study roles perform which
          functions. It combines a narrative overview with a structured requirements table, and it
          is the top-level document that anchors the entire validation package. Every downstream
          specification traces back to the URS.
        </p>

        <h4 className="mt-4 font-semibold text-gray-800">Functional Requirements Specification (FRS)</h4>
        <p>
          The FRS describes the functional behavior of the study's forms, edit checks, derivations,
          and randomization logic in the EDC. It is derived from the URS and translates the user
          requirements into testable functional behavior.
        </p>

        <h4 className="mt-4 font-semibold text-gray-800">System Design Specification (SDS)</h4>
        <p>
          The SDS describes how the study is architected inside the EDC. It covers the combined
          design overview including the visit schema, arm routing, and the overall data flow
          through the system.
        </p>

        <h4 className="mt-4 font-semibold text-gray-800">Application Design Specification (ADS)</h4>
        <p>
          The ADS provides form-level design detail: the field layouts, the field order, the skip
          logic between fields, and the role-based visibility rules that control which user can see
          which content.
        </p>

        <h4 className="mt-4 font-semibold text-gray-800">Audit Trail Design Specification (ATDS)</h4>
        <p>
          The ATDS documents the study-specific audit trail expectations, including the
          reason-for-change requirements on each field and the electronic signature points across
          the study workflow.
        </p>

        <h4 className="mt-4 font-semibold text-gray-800">Security and Access Control Specification (SACS)</h4>
        <p>
          The SACS is the study permission matrix. It defines which study role can read, edit, and
          sign each form and each field, and serves as the basis for role configuration in the EDC.
        </p>

        <h4 className="mt-4 font-semibold text-gray-800">Interface and Integration Specification (IIS)</h4>
        <p>
          The IIS documents the study's external data flows, including connections to central
          laboratories, IRT, ePRO, coding dictionaries, and EHR systems. It defines the data formats,
          the transfer cadence, and the reconciliation expectations for each external source.
        </p>

        <h3 className="mt-6 font-semibold text-gray-900">Standalone Sub-Documents</h3>
        <p>
          Standalone Sub-Documents are individual attachment-level documents that can be generated
          independently, without producing their parent Combined Document. Use these when your
          organization's quality system wants a particular deliverable as its own controlled
          document rather than as part of a larger one.
        </p>

        <h4 className="mt-4 font-semibold text-gray-800">System Requirements (standalone)</h4>
        <p>
          A narrative system-requirements document. Generate this when your organization wants a
          standalone SR deliverable separate from the URS.
        </p>

        <h4 className="mt-4 font-semibold text-gray-800">System Requirements Attributes (standalone)</h4>
        <p>
          A tabular field-level attribute specification referencing parent SR numbers, presented in
          a seven-column format (SR number, version, attribute number, attribute, acceptable values,
          codes, and comments).
        </p>

        <h4 className="mt-4 font-semibold text-gray-800">DB Physical Model (standalone)</h4>
        <p>
          A narrative description of the study database design covering the form-to-table mapping,
          the visit schema, and arm routing. Generate this when your organization wants the
          database design as its own deliverable separate from the SDS.
        </p>

        <h4 className="mt-4 font-semibold text-gray-800">DB Attributes (standalone)</h4>
        <p>
          A tabular field-level data specification including columns for variable, label, type,
          length, codelist, CDASH, and SDTM. Generate this when your organization wants the variable
          catalog as a separate attachment.
        </p>

        <h4 className="mt-4 font-semibold text-gray-800">Interface Features (standalone)</h4>
        <p>
          A narrative description of the study form and screen behaviors, including visit gating,
          skip logic, query flow, and role visibility.
        </p>

        <h4 className="mt-4 font-semibold text-gray-800">Interface Attributes (standalone)</h4>
        <p>
          A tabular field-level UI specification covering control type, required status, read-only
          status, default values, skip logic, edit check references, and help text.
        </p>

        <h3 className="mt-6 font-semibold text-gray-900">Testing and Qualification</h3>
        <p>
          Testing and Qualification documents support the execution and documentation of the study
          build qualification. They are what your validation lead and your independent reviewer
          will use to confirm the build is fit for purpose.
        </p>

        <h4 className="mt-4 font-semibold text-gray-800">Test Plan</h4>
        <p>
          The Test Plan is the umbrella plan that scopes the test strategy, the test environment,
          the roles, the schedule, the entry and exit criteria, and the acceptance criteria. It
          references the UAT Protocol, the Software Systems Testing Procedure, and the Test
          Incident Report.
        </p>

        <h4 className="mt-4 font-semibold text-gray-800">UAT Protocol</h4>
        <p>
          The UAT Protocol verifies that the study build in the EDC matches the protocol for
          visits, forms, edit checks, randomization, blinding, calculations, workflow, data
          exports, and the audit trail.
        </p>

        <h4 className="mt-4 font-semibold text-gray-800">UAT Report</h4>
        <p>
          The UAT Report is a fillable template for the UAT execution record. It includes
          pass/fail tabulation, a deviation log, an outstanding-issues list, and a multi-functional
          approval block. Download the template and complete the test results, deviations, and
          signatures during execution of the UAT Protocol.
        </p>

        <h4 className="mt-4 font-semibold text-gray-800">Software Systems Testing Procedure</h4>
        <p>
          The Software Systems Testing Procedure is a field-level test procedure with one Testing
          Procedure Form table for each CRF form. Test IDs, inputs, and expected outcomes are
          generated deterministically from the data dictionary so the procedure is reproducible and
          aligned to the variables you have approved.
        </p>

        <h4 className="mt-4 font-semibold text-gray-800">Test List</h4>
        <p>
          The Test List is a tabular inventory of every test procedure case, mapped to the system
          requirement numbers it verifies, plus a test type classification. Generate the Test List
          after the Software Systems Testing Procedure has been generated.
        </p>

        <h4 className="mt-4 font-semibold text-gray-800">Test Incident Report Template</h4>
        <p>
          A Word template for recording incidents observed during test procedure execution. It is
          pre-populated with the study metadata. The incident rows are filled in by hand during
          test execution.
        </p>

        <h4 className="mt-4 font-semibold text-gray-800">Testing Report Template</h4>
        <p>
          A Word template for documenting test execution outcomes including features tested,
          features not tested, the test methodology, and the execution summary. It is pre-populated
          with the study metadata.
        </p>

        <h4 className="mt-4 font-semibold text-gray-800">Mock Data Generator</h4>
        <p>
          The Mock Data Generator produces realistic synthetic test records — one CSV file per CRF
          form — for use in UAT, training, and database validation. The generator detects your
          project's EDC target and emits the data in the appropriate import format for that
          platform.
        </p>
      </>
    ),
  },
  {
    id: 'documents-tmf',
    title: 'Documents and TMF',
    content: (
      <>
        <p>
          Documents and TMF is the workspace where you manage the Trial Master File for your
          study. The TMF is organized according to the DIA Reference Model into ten zones covering
          everything from trial management through data management. Each zone groups the essential
          documents that belong there.
        </p>
        <p>
          The essential documents within each zone are further organized into three lifecycle
          categories based on ICH E6 Good Clinical Practice: <strong>Before the trial</strong>,{' '}
          <strong>During the trial</strong>, and <strong>After the trial</strong>. Each document
          entry shows its current status, its location (sponsor, site, or both), and its version.
        </p>
        <p>
          As you complete and file each essential document, update its status on this page so the
          TMF reflects the actual state of your filing. The status field gives you a working view
          of TMF readiness at any point in the study lifecycle.
        </p>
      </>
    ),
  },
  {
    id: 'dashboard',
    title: 'Dashboard',
    content: (
      <>
        <p>
          The Dashboard shows a summary of the project's current state across all workflow steps.
          It gives you a quick view of what has been completed and what still needs your attention,
          so you can decide where to focus your next session. From the dashboard you can open any
          project to drill into the workflow steps, or start a new project.
        </p>
      </>
    ),
  },
  {
    id: 'team',
    title: 'Team',
    content: (
      <>
        <p>
          The Team page is where you manage project membership. Each team member is assigned an
          access role that controls what they can do in the project. The available access roles
          are <strong>Owner</strong>, <strong>Manager</strong>, <strong>Editor</strong>,
          <strong> Reviewer</strong>, and <strong>Viewer</strong>.
        </p>
        <p>
          In addition to the access role, each member can have per-privilege overrides. These
          overrides let you fine-tune what an individual member can do beyond the defaults of their
          role. The available privileges include generating documents, uploading files, downloading
          files, editing content, approving documents, commenting, and managing members. For
          example, you might grant a Reviewer the ability to comment without giving them broader
          editing rights.
        </p>
        <p>
          Owners have two additional capabilities that cannot be overridden by privilege settings:
          transferring ownership of the project to another member, and deleting the project
          entirely. These actions are reserved to the Owner role by design.
        </p>
        <p>
          To add a team member, click <strong>Invite Member</strong> and enter their email address
          and the access role you want to assign. They will receive an invitation that they must
          accept before they gain access to the project. You can revoke a pending invitation at any
          time before it has been accepted.
        </p>
      </>
    ),
  },
  {
    id: 'activity',
    title: 'Activity',
    content: (
      <>
        <p>
          The Activity page shows a chronological log of changes made to the project. Every action
          that modifies project data is recorded with the user who performed it, the time the
          action occurred, and what changed. Use this page to investigate when something happened,
          to confirm a colleague's work, or to retrace your own steps after a complex editing
          session.
        </p>
        <p>
          The Activity log is a sponsor-governance audit trail for tracking who did what and when.
          It is not a 21 CFR Part 11 audit trail. Part 11 compliance applies to the downstream EDC
          system that uses the deliverables produced here, not to this platform.
        </p>
      </>
    ),
  },
  {
    id: 'study-settings',
    title: 'Study Settings',
    content: (
      <>
        <p>
          Study Settings contains configuration options for the project, including the study name
          and other project-level metadata. Open it from the sidebar at any time to review or
          update the project's basic information.
        </p>
      </>
    ),
  },
  {
    id: 'regulatory-standards',
    title: 'Regulatory Standards Reference',
    content: (
      <>
        <p>
          DocumentSpark generates documents that are grounded in established regulatory and
          industry standards. This section lists the primary standards referenced throughout the
          platform so that you understand the basis for the content you receive. Where a version is
          important to what is generated, the version is called out.
        </p>

        <h3 className="mt-6 font-semibold text-gray-900">CDISC Clinical Data Standards</h3>
        <p>
          The CDISC family of standards governs the structure of clinical data from collection
          through submission. Several CDISC standards are used in this platform.
        </p>
        <p className="mt-3">
          <strong>CDASH (Clinical Data Acquisition Standards Harmonization).</strong> CDASH defines
          the standard structure for case report form data collection. Your generated CRFs and your
          data dictionary are aligned to CDASH IG v2.2 (November 2022). CDASH is the anchoring
          authority for the CRF Builder, including form suggestions and variable generation. The
          CDASH SAE Supplement v2.0 is the canonical reference for the SAE Reconciliation
          Procedure, where it bridges CDASH safety data to E2B(R3) regulatory reporting.
        </p>
        <p className="mt-3">
          <strong>SDTM and SDTMIG.</strong> The Study Data Tabulation Model and its Implementation
          Guide define how clinical data is organized for regulatory submission. The platform
          selects the SDTM version based on your study start date against the FDA Data Standards
          Catalog: studies starting on or after March 15, 2023 use SDTM 1.7 with SDTMIG 3.3;
          earlier studies use SDTM 1.5 with SDTMIG 3.2. The SDTM Mapping Specification, the
          Define.xml export, and the submission package all use the version pinned for your study.
        </p>
        <p className="mt-3">
          <strong>Define-XML.</strong> Define-XML is the standard machine-readable metadata format
          for SDTM submissions. The version is selected automatically alongside SDTM: Define-XML 2.1
          for post-2023 studies, Define-XML 2.0 for earlier studies.
        </p>
        <p className="mt-3">
          <strong>CDISC Controlled Terminology.</strong> Controlled Terminology is the official
          codelist library that governs allowable values in SDTM. The platform uses the 2025-09-26
          quarterly release. Codelist references in your generated documents draw from this
          release.
        </p>
        <p className="mt-3">
          <strong>CDISC ODM.</strong> The Operational Data Model is referenced as a vendor
          interchange format for EDC platforms (Medidata Rave, OpenClinica, REDCap, Castor, and
          Dacima). The platform does not currently emit ODM XML itself; the ODM references
          describe the import capabilities of target EDC platforms.
        </p>

        <h3 className="mt-6 font-semibold text-gray-900">ICH Guidelines</h3>
        <p>
          The International Council for Harmonisation (ICH) guidelines provide the global
          framework for clinical trial conduct and safety reporting. Several ICH guidelines are
          referenced throughout the platform.
        </p>
        <p className="mt-3">
          <strong>ICH E6 Good Clinical Practice.</strong> ICH E6 is the foundational guideline for
          the conduct of clinical trials. It is the most-cited standard across this platform's
          generated documents. Different documents reference different revisions: some refer to
          E6(R2), others to E6(R3) (2025), depending on which revision was active when that
          deliverable's templates were most recently updated. The Documents and TMF page is
          organized around ICH E6's lifecycle categorization of essential documents (before, during,
          and after the trial).
        </p>
        <p className="mt-3">
          <strong>ICH E3 (Clinical Study Reports).</strong> E3 governs the structure and content of
          clinical study reports. It is referenced in the CRF Instruction Guide.
        </p>
        <p className="mt-3">
          <strong>ICH E2A, E2B(R3), E2D(R1), and E2F.</strong> The E2 series covers safety
          reporting, including expedited reporting standards (E2A), electronic transmission of
          individual case safety reports (E2B(R3)), post-approval safety data management (E2D(R1)),
          and development safety update reports (E2F). The SAE Reconciliation Procedure is
          grounded in this series.
        </p>

        <h3 className="mt-6 font-semibold text-gray-900">FDA Regulations and Guidance</h3>
        <p>
          U.S. Food and Drug Administration regulations and guidance documents establish the
          regulatory expectations for clinical trials conducted under FDA jurisdiction. Several FDA
          references inform the generated documents.
        </p>
        <p className="mt-3">
          <strong>21 CFR Part 11.</strong> Part 11 governs electronic records and electronic
          signatures in FDA-regulated environments. Part 11 applies to the downstream EDC system
          that ultimately uses the deliverables produced here; DocumentSpark itself is
          upstream of EDC and is out of scope for Part 11. The Audit Trail Design Specification
          references §11.10(e) when documenting the audit trail expectations your EDC must satisfy.
        </p>
        <p className="mt-3">
          <strong>21 CFR 312.32.</strong> The IND safety reporting regulation. Cited in the SAE
          Reconciliation Procedure as one of the regulatory bases for the procedure.
        </p>
        <p className="mt-3">
          <strong>21 CFR 50.27.</strong> The informed consent documentation regulation. Cited in
          the informed consent narrative.
        </p>
        <p className="mt-3">
          <strong>FDA Study Data Technical Conformance Guide (May 2023).</strong> The controlling
          FDA guidance for SDTM and Define-XML submissions. The SDTM Mapping Specification cites
          this guide as its conformance authority.
        </p>
        <p className="mt-3">
          <strong>FDA Data Standards Catalog.</strong> The catalog of supported FDA data standards
          versions. The platform queries the catalog to choose the correct SDTM, SDTMIG, Define-XML,
          and Controlled Terminology versions for your study based on its start date.
        </p>
        <p className="mt-3">
          <strong>FDA Computer Software Assurance (CSA) guidance.</strong> CSA is the modern
          risk-based framework for software validation in FDA-regulated environments. It is
          referenced in the Study Build validation suite as the framework under which the generated
          design and test documents are accepted as evidence.
        </p>
        <p className="mt-3">
          <strong>FDA Guidance for Computerized Systems Used in Clinical Investigations.</strong>{' '}
          The 2007 guidance and the October 2024 Q&A on Electronic Records and Signatures are
          referenced in the Audit Trail Design Specification.
        </p>

        <h3 className="mt-6 font-semibold text-gray-900">European Regulations and Guidance</h3>
        <p>
          <strong>EU GMP Annex 11.</strong> The European Union's equivalent to FDA's electronic
          records requirements. Annex 11 is referenced in the Application Design Specification, the
          Audit Trail Design Specification, the External Data Transfer Specification, and the DB
          Physical Model as the EU counterpart to Part 11.
        </p>
        <p className="mt-3">
          <strong>EMA Guideline on Computerised Systems and Electronic Data in Clinical Trials
          (2023).</strong> Effective September 10, 2023, this guideline sets EMA expectations for
          electronic data and computerized systems. It is referenced in the Audit Trail Design
          Specification and the External Data Transfer Specification.
        </p>

        <h3 className="mt-6 font-semibold text-gray-900">Software Quality and Validation</h3>
        <p>
          <strong>GAMP 5.</strong> ISPE's GAMP 5 (A Risk-Based Approach to Compliant GxP
          Computerized Systems) is the framework underpinning the SDLC validation suite. The URS,
          FRS, SDS, ADS, ATDS, SACS, IIS, and the database and interface specifications all align
          to GAMP 5's risk-based approach.
        </p>
        <p className="mt-3">
          <strong>SCDM GCDMP (Good Clinical Data Management Practices).</strong> The Society for
          Clinical Data Management's GCDMP is the industry framework for data management practice.
          The Data Management Plan is structured around GCDMP, and per-chapter guidance is
          embedded across the DMP sections. The Medical Coding Specification, the External Data
          Transfer Specification, and the Data Review and Cleaning Plan all draw on relevant GCDMP
          chapters.
        </p>

        <h3 className="mt-6 font-semibold text-gray-900">Coding Dictionaries and Reference Models</h3>
        <p>
          <strong>MedDRA, WHODrug, and LOINC.</strong> MedDRA is used for adverse event and medical
          history coding; WHODrug for concomitant medications; LOINC for laboratory tests. The
          Medical Coding Specification documents the coding conventions and dictionary versions for
          your study. The dictionaries themselves are not bundled with this platform; coding is
          performed downstream by your data management team using your organization's licensed
          dictionary copies.
        </p>
        <p className="mt-3">
          <strong>CTCAE v5.0 (Common Terminology Criteria for Adverse Events).</strong> Used in
          oncology and other studies for grading adverse events. Referenced in the data management
          prompts and the CRF Instruction Guide.
        </p>
        <p className="mt-3">
          <strong>DIA TMF Reference Model.</strong> The Drug Information Association's reference
          model for the Trial Master File, organized into ten zones. The Documents and TMF page uses
          the DIA model for its zone structure.
        </p>
        <p className="mt-3">
          <strong>HIPAA.</strong> The U.S. Health Insurance Portability and Accountability Act is
          referenced descriptively when discussing EDC vendor compliance posture. Clinical
          Intelligence does not handle protected health information, so HIPAA does not apply to the
          platform itself.
        </p>
      </>
    ),
  },
];

export default function Help() {
  const [activeId, setActiveId] = useState<string>(SECTIONS[0].id);
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});

  const sectionIds = useMemo(() => SECTIONS.map(s => s.id), []);

  useEffect(() => {
    // Track the active section as the user scrolls so the TOC highlight
    // follows along. We bias the active line toward the top of the viewport
    // (rootMargin shifts the intersection band up by ~25% of viewport height)
    // so a section is considered "current" as it crosses near the top, not
    // when it's centered.
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter(e => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) {
          setActiveId((visible[0].target as HTMLElement).id);
        }
      },
      {
        rootMargin: '-80px 0px -70% 0px',
        threshold: 0,
      }
    );

    sectionIds.forEach(id => {
      const el = sectionRefs.current[id];
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [sectionIds]);

  const handleTocClick = (id: string) => {
    const el = sectionRefs.current[id];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="flex gap-8 max-w-7xl">
      {/* Left TOC */}
      <aside className="w-60 flex-shrink-0">
        <div className="sticky top-6">
          <p className="text-[10px] uppercase tracking-widest font-medium text-gray-500 mb-3 px-3">
            Contents
          </p>
          <nav className="space-y-0.5">
            {SECTIONS.map((section, idx) => {
              const isActive = activeId === section.id;
              return (
                <button
                  key={section.id}
                  onClick={() => handleTocClick(section.id)}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                    isActive
                      ? 'bg-primary-50 text-primary-700 font-medium'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  <span className="text-gray-400 text-xs mr-2">{idx + 1}.</span>
                  {section.title}
                </button>
              );
            })}
          </nav>
        </div>
      </aside>

      {/* Right content */}
      <div className="flex-1 min-w-0">
        <header className="mb-8 pb-6 border-b border-gray-200">
          <h1 className="text-3xl font-bold text-gray-900">Help</h1>
          <p className="mt-2 text-gray-600">
            A reference guide to the DocumentSpark platform. Each section walks you through
            one workflow step or one platform area.
          </p>
        </header>

        <div className="space-y-12 pb-24">
          {SECTIONS.map((section, idx) => (
            <section
              key={section.id}
              id={section.id}
              ref={el => { sectionRefs.current[section.id] = el; }}
              className="scroll-mt-6"
            >
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                <span className="text-gray-400 font-normal mr-2">{idx + 1}.</span>
                {section.title}
              </h2>
              <div className="prose prose-gray max-w-none text-gray-700 leading-relaxed">
                {section.content}
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
